import {useDispatch} from 'react-redux'
import {useState,useRef,useEffect,useCallback} from 'react'
import { Line,Scatter } from 'react-chartjs-2'
import HowTo from './HowTo'
import Webcam from "react-webcam"
import useCountDown from "react-countdown-hook"
import useCurrentUser from '../../hooks/useCurrentUser'
import useWindowSize from '../../hooks/useWindowSize'
import * as poseDetection from '@tensorflow-models/pose-detection'
import * as tf from '@tensorflow/tfjs-core'
// Register one of the TF.js backends.
import '@tensorflow/tfjs-backend-webgl'
import {
	isWebview,
	roundDecimal,
	normalizeX,
	repConsistency,
	tempoConsistency,
	depthScore,
	extractRepSlices,
	nonMonotonicIndexes,
	argMin,
	argMax,
	getKneeCaveTimeMap,
	restConsistency,
	kneeArticulationConsistency,
	centerLineConsistency,
	repsCenterLineScore,
	chestCenter,
	hipCenter,
	useAnimationFrame,
	poseFemurLength,
	poseHeight,
	balancingSide,
	calculateCenterStatus,
	calculateShoulderTilt,
	calculateHipTilt,
	tiltScore,
	kneeHeightScore,
	nearest
} from './util'
import {
	removeLeaderboardRecord,
	editLeaderboardRecord,
	addLeaderboardRecord,
	setMetricCreator,
	setShow
} from './slice'
import styles from './AIDemo.module.css'
import Guides from './Guides'
import {dateToYMD} from '../../util/datetime'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import KeypointLogViewer from './KeypointLogViewer'
import Leaderboard from './Leaderboard'
import testData from './testBalanceData.json'

const SCORE_THRESHOLD = 0.35

const detectorConfig = {
	modelType: poseDetection.movenet.modelType.SINGLEPOSE_THUNDER,
	enableTracking: true,
	trackerType: poseDetection.TrackerType.BoundingBox,
}

const BalanceDemo = ()=>{
	const dispatch = useDispatch()
	const [windowWidth,windowHeight] = useWindowSize()

	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const ratio = 1//windowWidth/windowHeight

	const [detector,setDetector] = useState(null)
	const [showHowTo,setShowHowTo] = useState(false)
	const [showResults,setShowResults] = useState(false)
	const [showLeaderboard,setShowLeaderboard] = useState(false)
	const testVidRef = useRef(null)
	const testVidLoadedRef = useRef(false)

	const canvasRef = useRef(null)

	const goalCount = 10
	const countRef = useRef(0)
	const countSpanRef = useRef(null)

	const [timeLeft, { start:startCoundown,reset:resetCountdown}] = useCountDown(3000, 1000)
	const countingDownRef = useRef(false)

	const initializeGuides = async ()=>{
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
		if (poses.length === 0) return
		const keypoints = poses[0].keypoints

		const leftHip = keypoints[11]
		const rightHip = keypoints[12]

		const idealKneeHeight = (leftHip.y + rightHip.y)/2

		resultsRef.current = emptyResults()
		countRef.current = 0
		squatStateRef.current = 'standing'
		countSpanRef.current.innerText = countRef.current
		squatStateSpanRef.current.innerText = stateDisplayTextMap['standing']
		//average of two ankles
		const bottomY = Math.max(keypoints[15].y,keypoints[16].y) - poseHeight(keypoints)/25
		guidesRef.current = {
			bottomY:Math.min(bottomY,320),
			idealKneeHeight
		}
		setTimeout(()=>{
			setShowGuides(true)
		},300)
	}

	useEffect(()=>{
		if(!overlayRef.current||!countingDownRef.current) return
		if(timeLeft===0&&countingDownRef.current){
			countingDownRef.current = false
			initializeGuides()
			overlayRef.current.innerText = 'GO!'
			setTimeout(()=>{setShowOverlay(false)},1500)
		}else{
			overlayRef.current.innerText = Math.ceil(timeLeft/1000)
		}
	},[timeLeft,countingDownRef])

	//stores results from squat analysis
	const emptyResults = ()=>({
		keypointLog:[],
		//which foot (left/right) is being balanced on
		balanceFoot:null,
		chestPosition:[],
		shoulderTilt:[],
		left:{
			start:0,
			end:0,
			kneePosition:[]
		},
		right:{
			start:0,
			end:0,
			kneePosition:[]
		}
	})

	const [results,setResults] = useState(testData)//useState(emptyResults())//
	const resultsRef = useRef(testData)//useRef(emptyResults())//

	const posesRef = useRef([])
	const [showGuides,setShowGuides] = useState(false)
	const [showOverlay,setShowOverlay] = useState(false)
	const overlayRef = useRef(null)
	const guidesRef = useRef({
		bottomY:100,
		idealKneeHeight:120
	})

	const [name,setName] = useState('')
	const [email,setEmail] = useState('')

	useEffect( async () => {
		setDetector(
			await poseDetection.createDetector(
				poseDetection.SupportedModels.MoveNet,
				detectorConfig
			)
		)
	},[])

	//console.log('render')
	//reference to if we have initialized the squat guides
	const initializedRef = useRef(false)
	const trackResultsRef = useRef(false)
	const webcamRef = useRef(null)

	const stateDisplayTextMap = {
		standing:'Standing',
		balancing:'balancing',
		waiting:'Waiting',
		setup:'Get Ready',
		switching:'Switch Sides'
	}

	const initState = 'waiting'
	const squatStateRef = useRef(initState)
	const squatStateSpanRef = useRef(null)

	const infer = useCallback( async () => {
		//console.log(store.getState())
		//map from current state to trigger for transitioning to next
		//state
		const onTransition = (t)=>{
			if(!squatStateSpanRef.current) return
			squatStateSpanRef.current.innerText = stateDisplayTextMap[squatStateRef.current]
		}
		
		//runs on every inference to log data continuously (eg nose & knee positions)
		const logger = (keypoints,t)=>{
			//console.log(keypoints)
			resultsRef.current.shoulderTilt.push({
				angle:calculateShoulderTilt(keypoints),
				t
			})
		}

		//These are triggers that get fired if in the current state
		//For example: if the current state is standing, then only the 
		//trigger for standing gets fired 
		const transitionTriggers = {
			setup:(keypoints,t)=>{
				//when elbow and wrist keypoints go above shoulders
				//then transition into standing

				//left arm keypoints
				const lWrist = keypoints[10]
				const lElbow = keypoints[8]
				const lShoulder = keypoints[6]

				const lArmUp = (
					lWrist.y - 20 < lShoulder.y &&
					lElbow.y - 20 < lShoulder.y
				)

				//right arm keypoints
				const rWrist = keypoints[9]
				const rElbow = keypoints[7]
				const rShoulder = keypoints[5]

				const rArmUp = (
					rWrist.y - 20 < rShoulder.y &&
					rElbow.y - 20 < rShoulder.y
				)

				if (lArmUp&&rArmUp&&!countingDownRef.current) {
					startCoundown()
					countingDownRef.current = true
					setShowOverlay(true)
				}

			},
			standing:(keypoints,t)=>{
				const leftAnkle = keypoints[15]
				const rightAnkle = keypoints[16]
				//if in standing state transition to balancing 
				//when one ankle goes above the threshold

				const leftAnkleLifted = leftAnkle.y <= guidesRef.current.bottomY
				const rightAnkleLifted = rightAnkle.y <= guidesRef.current.bottomY
				if ( leftAnkleLifted || rightAnkleLifted ) {
					squatStateRef.current = 'balancing'
					if (leftAnkleLifted) {
						resultsRef.current.balanceFoot = 'right'
						resultsRef.current.right.start = t
					} else {
						resultsRef.current.balanceFoot = 'left'
						resultsRef.current.left.start = t	
					}
				}
			},
			switching:(keypoints,t)=>{
				const leftAnkle = keypoints[15]
				const rightAnkle = keypoints[16]
				//if in standing state transition to balancing 
				//when one ankle goes above the threshold

				const leftAnkleLifted = leftAnkle.y <= guidesRef.current.bottomY
				const rightAnkleLifted = rightAnkle.y <= guidesRef.current.bottomY
				const currentBalanceFoot = resultsRef.current.balanceFoot
				if ( 
					//if was balancing on left foot and is now balancing on right foot
					(leftAnkleLifted&&currentBalanceFoot==='left') || 
					//if was balancing on right foot and is now balancing on left foot
					(rightAnkleLifted &&currentBalanceFoot==='right')
				) {
					squatStateRef.current = 'balancing'
					if(leftAnkleLifted){
						resultsRef.current.balanceFoot = 'right'
						resultsRef.current.right.start = t
					}else {
						resultsRef.current.balanceFoot = 'left'
						resultsRef.current.left.start = t	
					}
				}
			},
			balancing:(keypoints,t)=>{
				/*
				resultsRef.current.chestPosition.push({
					x:320-chestCenter(keypoints,'x'),
					y:chestCenter(keypoints,'y'),
					status:calculateCenterStatus(320-chestCenter(keypoints),guidesRef.current),
					t
				})*/
				const leftAnkle = keypoints[15]
				const leftKnee = keypoints[13]

				const rightAnkle = keypoints[16]
				const rightKnee = keypoints[14]

				const leftAnkleLifted = leftAnkle.y <= guidesRef.current.bottomY
				const rightAnkleLifted = rightAnkle.y <= guidesRef.current.bottomY
				//if in balancing state transition to stand 
				//when one both ankles below threshold
				if ( !leftAnkleLifted && !rightAnkleLifted ) {
					squatStateRef.current = 'standing'
					resultsRef.current[resultsRef.current.balanceFoot].end = t
				}
				if(leftAnkleLifted){
					resultsRef.current.right.kneePosition.push({
						...leftKnee,
						t
					})
				}
				if(rightAnkleLifted){
					resultsRef.current.left.kneePosition.push({
						...rightKnee,
						t
					})
				}
				if (leftAnkleLifted||rightAnkleLifted) {
					const start = (
						leftAnkleLifted
						? resultsRef.current.right.start
						: resultsRef.current.left.start
					)
					const ellapsed = (t - start)/1000
					countSpanRef.current.innerText  = Math.min(ellapsed,goalCount).toFixed(2)
					if(ellapsed>=goalCount){
						squatStateRef.current = 'switching'
						resultsRef.current[resultsRef.current.balanceFoot].end = t
						if(!(resultsRef.current.left.end&&resultsRef.current.right.end)){
							overlayRef.current.innerText = 'Switch Sides'
							setShowOverlay(true)
							setTimeout(()=>{setShowOverlay(false)},2200)
						}
					}
				}
				//if has results for both feet end the tracking and show results
				if(resultsRef.current.left.end&&resultsRef.current.right.end){
					overlayRef.current.innerText = 'Done'
					setShowOverlay(true)
					setTimeout(()=>{
						cancelInferencing()
						squatStateRef.current = initState
						setShowResults(true)
						console.log(resultsRef.current)
						setShowOverlay(false)
					},2200)
				}
			},
		}

		if (!webcamRef.current.video||!detector) {
			//console.log('Waiting for dependencies')
		} else {
			const ctx = canvasRef.current.getContext("2d")
			ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
			if (!initializedRef.current||!trackResultsRef.current) {
				//console.log('Not initialized')
				return
			}
			try {
				//console.log(`inferencing`)
				const poses = await detector.estimatePoses(canvasRef.current)
				posesRef.current = poses
				if ( poses.length > 0 ) {
					const keypoints = poses[0].keypoints
					const nose = keypoints[0]
					const t = Date.now()
					resultsRef.current.keypointLog.push({keypoints,t})
					if ( nose.score >= SCORE_THRESHOLD ) {
						if(squatStateRef.current!=='setup') logger(keypoints,t) 
						const transitionTrigger = transitionTriggers[squatStateRef.current]
						if (transitionTrigger) {
							transitionTrigger(keypoints,t)
							onTransition(t)
						}
					}
				}
			} catch(err) {
				//console.log(err)
			}
		}
	},[detector,webcamRef,guidesRef])

	const {
		cancel:cancelInferencing,
		start:startInferencing
	} = useAnimationFrame(infer)

	const [started,setStarted] = useState(false)

	const startDemo = async ()=>{
		setStarted(true)
		cancelInferencing()
		resetCountdown()
		setShowGuides(false)

		resultsRef.current = emptyResults()
		countRef.current = 0
		countSpanRef.current.innerText = countRef.current
		squatStateSpanRef.current.innerText = stateDisplayTextMap['setup']

		initializedRef.current = true
		squatStateRef.current = 'setup'
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		//testVidRef.current.play()
		startInferencing()
		//run a single inference to load model unto GPU
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(webcamRef.current.video,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
	}

	const restartDemo = ()=>{
		setShowResults(false) 
		setShowGuides(false)
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		countingDownRef.current = false
		resultsRef.current = emptyResults()
		countRef.current = 0
	}

	const stopDemo = ()=>{
		setShowOverlay(false)
		setShowGuides(false)
		countingDownRef.current = false
		resetCountdown()
		cancelInferencing()
		setStarted(false)
		resultsRef.current = emptyResults()
		countRef.current = 0
		countSpanRef.current.innerText = countRef.current
		squatStateSpanRef.current.innerText = stateDisplayTextMap[initState]
	}

	const handleStartTracking = ()=>{
		resultsRef.current = emptyResults()
		trackResultsRef.current=true
		startInferencing()
	}

	const handleReset = ()=>{
		cancelInferencing()
	}

	const [saving,setSaving] = useState(false)

	const logInference = async ()=>{
		const ctx = canvasRef.current.getContext("2d")
		ctx.drawImage(testVidRef.current,0,0,320,320/ratio)
		const poses = await detector.estimatePoses(canvasRef.current)
		console.log(poses)
	}

	const calculateScores = ()=>{
		const leftShoulderTiltScore = tiltScore(
			resultsRef.current.shoulderTilt.filter(
				p => (
					resultsRef.current.right.start
					<=
					p.t
					&&
					p.t
					<=
					resultsRef.current.right.end
				)
			).map( p => ({
				x:p.t - resultsRef.current.shoulderTilt[0].t,
				y:p.angle,
				status:p.status
			}))
		)
		const rightShoulderTiltScore = tiltScore(
			resultsRef.current.shoulderTilt.filter(
				p => (
					resultsRef.current.right.start
					<=
					p.t
					&&
					p.t
					<=
					resultsRef.current.right.end
				)
			).map( p => ({
				x:p.t - resultsRef.current.shoulderTilt[0].t,
				y:p.angle,
				status:p.status
			}))
		)

		const leftTimeScore = ((resultsRef.current.left.end - resultsRef.current.left.start)/100)
		const rightTimeScore = ((resultsRef.current.right.end - resultsRef.current.right.start)/100)

		const leftKneeHeightScore = kneeHeightScore(
			resultsRef.current.left.kneePosition,
			guidesRef.current.idealKneeHeight,
			guidesRef.current.bottomY
		)

		const rightKneeHeightScore = kneeHeightScore(
			resultsRef.current.right.kneePosition,
			guidesRef.current.idealKneeHeight,
			guidesRef.current.bottomY
		)

		const average = (
			leftShoulderTiltScore +
			rightShoulderTiltScore +
			leftTimeScore +
			rightTimeScore +
			leftKneeHeightScore +
			rightKneeHeightScore
		)/6

		return {
			leftShoulderTiltScore,
			rightShoulderTiltScore,
			leftTimeScore,
			rightTimeScore,
			leftKneeHeightScore,
			rightKneeHeightScore,
			average
		}
	}

	const handleAddToProgress = ()=>{
		const {
			leftShoulderTiltScore,
			rightShoulderTiltScore,
			leftTimeScore,
			rightTimeScore,
			leftKneeHeightScore,
			rightKneeHeightScore,
			average
		} = calculateScores()


		dispatch(setMetricCreator({
			show:true,
			category:'AI Single Leg Balance Assessment',
			date:dateToYMD(new Date()),
			aiLog:JSON.stringify(results),
			metrics:[
				{
					name:'Left Leg Balance',
					description:'',
					//implicit
					unit_id:1,
					value:leftShoulderTiltScore
				},{
					name:'Right Leg Balance',
					description:'',
					//implicit
					unit_id:1,
					value:rightShoulderTiltScore
				},{
					name:'Left Leg Balance Time',
					description:'',
					//implicit
					unit_id:1,
					value:leftTimeScore
				},{
					name:'Right Leg Balance Time',
					description:'',
					//implicit
					unit_id:1,
					value:rightTimeScore
				},{
					name:'Left Leg Balance Knee Height',
					description:'',
					//implicit
					unit_id:1,
					value:leftKneeHeightScore
				},{
					name:'Right Leg Balance Knee Height',
					description:'',
					//implicit
					unit_id:1,
					value:rightKneeHeightScore
				},{
					name:'Total Balance Score',
					description:'',
					//implicit
					unit_id:1,
					value:average
				}
			]
		}))
	}

	return (
		<div className={styles.AIDemoWidget}>
			<div ref={overlayRef} style={{
				position:'absolute',
				width:'100vw',
				height:'100vh',
				background:'rgba(0,0,0,0.2)',
				color:'rgb(50,255,50)',
				display:showOverlay?'flex':'none',
				justifyContent:'center',
				fontWeight:600,
				alignItems:'center',
				fontSize:'120px',
				zIndex:3
			}}/>
			{!showResults&&
				<div className="m-auto">
					<div style={{position:'relative'}}>
						
						<svg style={{
							position:'absolute',
							top:0,left:0,
							zIndex:2,
							overflow:'visible',
							width: "100vw",
							height: "100vh",
						}}>
							{showGuides&&<Guides 
								guides={guidesRef.current} 
								ratio={1}
								scale={webcamRef.current.video.getBoundingClientRect().width/320}
							/>}
						</svg>
					</div>
					{/*
					<video 
						style={{
							height:`${windowHeight}px`,
							width:`${windowWidth}px`,
							display:'block'
						}}
						onLoadedData={()=>{testVidLoadedRef.current = true}}
						crossorigin='anonymous' 
						ref={testVidRef} 
						muted loop
						src="http://localhost/assets/videos/balance360x360.mp4"
					/>
					*/}
					<Webcam 
						style={{
							height:windowWidth>windowHeight?'100vh':'auto',
							width:windowWidth>=windowHeight?'auto':'100vw',
							display:'block',
							margin:'auto'
						}}
						ref={webcamRef}
						mirrored
						videoConstraints={{
							width: 720,
							height:720,
							facingMode:"user",
							deviceId:"c141e16a643707300abde9eaae8a522f531793da15be147c3e1d7f40dcdfaea7"
						}}
					/>
					<canvas 
						style={{display:'none'}} 
						width={320} height={320} 
						ref={canvasRef}
					/>
					<div style={{
						position:'absolute',
						top:'4px',
						right:'4px',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						borderRadius:'8px',
						zIndex:3,
						width:'200px'
					}}>
						<div 
							style={{fontSize:'40px',fontFamily:'monospace'}}
							className="text-center"
						>
							<span ref={countSpanRef}>{countRef.current}</span>
						</div>
						<div className="d-flex justify-content-between">
							<small className="mr-2" ref={squatStateSpanRef}>Waiting</small>
							<small className={Boolean(detector)?'text-success':'text-muted'}>
								{Boolean(detector)?'AI Downloaded':'Downloading AI...'}
							</small>
						</div>
					</div>
					<div style={{
						position:'absolute',
						bottom:0,
						left:0,
						display:'flex',
						flexWrap:'wrap',
						background:'rgba(255,255,255,0.9)',
						padding:'8px',
						zIndex:3
					}}>
						<Button
							className="mr-2"
							variant="outline-primary"
							onClick={()=>{
								setShowHowTo(p=>!p)
							}}
						>
							<i className="fa fa-question-circle" /> How To
						</Button>
						{!started&&
							<Button className="mr-2" onClick={startDemo} variant="outline-success">
								Start
							</Button>
						}
						{started&&
							<Button className="mr-2" onClick={stopDemo} variant="outline-danger">
								Stop
							</Button>
						}
						{!isWebview()&&
							<Button 
								className="mr-2"
								onClick={()=>{
									dispatch(setShow(false))
								}}
								variant="outline-danger" 
							>
								Close
							</Button>
						}
						{/*
						<Button 
							className="mr-2" 
							onClick={()=>{testVidRef.current.play()}} 
							variant="outline-primary"
						>
							Start Video
						</Button>
						<Button 
							className="mr-2" 
							onClick={(e)=>{
								e.preventDefault()
								testVidRef.current.pause()
							}} 
							variant="outline-primary"
						>
							Pause Video
						</Button>
						<Button 
							className="mr-2" 
							onClick={logInference} 
							variant="outline-primary"
						>
							Log Inference
						</Button>
						*/}
					</div>
				</div>
			}
			{showResults&&
				<div style={{
					width:'100vw',
					height:'100vh',
					overflow:'auto',
					padding:'16px'
				}}>
					<div style={{
						fontSize:'32px',
						textAlign:'center',
						marginTop:'44px',
						marginBottom:'16px'
					}}>
						Results Analysis
					</div>
					<KeypointLogViewer 
						id="balance"
						demo="balance"
						results={results}
					/>
					<div style={{width:'100%',height:'200px'}}>
						<Line
							options={{
								title:{
									display:true,
									text:`Balance`,
								},
								maintainAspectRatio:false,
								responsive:true,
								animation: {
									duration: 0
								},
								hover: {
									mode: 'nearest'
								},
								scales: {
									xAxes: [{
										ticks:{
											callback:(label)=>(label/1000).toFixed(1),
											maxTicksLimit:10
										},
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Time (seconds)'
										}
									}],//end xAxes
									yAxes: [
										{
											display:true,
											position:'top',
											ticks:{
												suggestedMin:15,
												suggestedMax:15
											},
											scaleLabel:{
												display:true,
												labelString:'Tilt Angle (degrees)'
											}
										}
									]//end yAxes
								}//end scales
							}}
							data={
								{
									labels:resultsRef.current.shoulderTilt.filter(
										p => (
											resultsRef.current.left.start
											<=
											p.t
											&&
											p.t
											<=
											resultsRef.current.left.end
										)
									).map((p)=>p.t - resultsRef.current.left.start),
									datasets: [
										{
											lineTension: 0,
											label: 'Shoulder Tilt (left leg)',
											data:resultsRef.current.shoulderTilt.filter(
												p => (
													resultsRef.current.left.start
													<=
													p.t
													&&
													p.t
													<=
													resultsRef.current.left.end
												)
											).map(p=>({
												x:p.t - resultsRef.current.shoulderTilt[0].t,
												y:roundDecimal(p.angle),
												status:p.status
											})),
											backgroundColor: "rgba(96, 191, 108,0.2)",
											borderColor: "rgba(96, 191, 108,1)"
										},{
											lineTension: 0,
											label: 'Shoulder Tilt (right leg)',
											data:resultsRef.current.shoulderTilt.filter(
												p => (
													resultsRef.current.right.start
													<=
													p.t
													&&
													p.t
													<=
													resultsRef.current.right.end
												)
											).map(p=>({
												x:p.t - resultsRef.current.right.start,
												y:roundDecimal(p.angle),
												status:p.status
											})),
											backgroundColor: "rgba(90, 124, 196,0.2)",
											borderColor: "rgba(90, 124, 196,1)",
										}
									]
								}
							}
						/>
					</div>
					<div style={{width:'100%',height:'200px'}}>
						<Line
							options={{
								title:{
									display:true,
									text:`Knee Height`,
								},
								maintainAspectRatio:false,
								responsive:true,
								animation: {
									duration: 0
								},
								hover: {
									mode: 'nearest'
								},
								scales: {
									xAxes: [{
										ticks:{
											callback:(label)=>(label/1000).toFixed(1),
											maxTicksLimit:10
										},
										display:true,
										scaleLabel:{
											display:true,
											labelString:'Time (seconds)'
										}
									}],//end xAxes
									yAxes: [
										{
											display:true,
											position:'top',
											scaleLabel:{
												display:true,
												labelString:'Height'
											}
										}
									]//end yAxes
								}//end scales
							}}
							data={
								{
									labels:resultsRef.current.left.kneePosition.map((p)=>p.t - resultsRef.current.left.start),
									datasets: [
										{
											lineTension: 0,
											label: 'On Left Foot',
											data:resultsRef.current.left.kneePosition.map(p=>({
												x:p.t - resultsRef.current.left.start,
												y:roundDecimal(320 - p.y),
											})),
											backgroundColor: "rgba(240, 198, 101,0.2)",
											borderColor: "rgba(240, 198, 101,1)",
										},{
											lineTension: 0,
											label: 'On Right Foot',
											data:resultsRef.current.right.kneePosition.map(p=>({
												x:p.t - resultsRef.current.right.start,
												y:roundDecimal(320 - p.y),
											})),
											backgroundColor: "rgba(187, 119, 224,0.2)",
											borderColor: "rgba(187, 119, 224,1)"
										},{
											lineTension: 0,
											label: 'ideal',
											data:resultsRef.current.left.kneePosition.map(()=>roundDecimal(guidesRef.current.idealKneeHeight)),
											backgroundColor: "rgba(111, 214, 115,0)",
											borderColor: "rgba(111, 214, 115,1)",
											pointRadius: 0
										}
									]
								}
							}
						/>
					</div>
					<div className="d-flex flex-wrap justify-content-between">
						<div 
							className="d-flex flex-column align-items-stretch" 
							style={{flex:'0 0 auto',width:'400px',maxWidth:'100%'}}
						>
							<div className="d-flex justify-content-between">
								<strong>Metric</strong>
								<strong>Score</strong>
							</div>
							{/*
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">Center Balance</div>
								<div>
									{centerLineConsistency(resultsRef.current.chestPosition).toFixed()}
								</div>
							</div>*/}
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">L. Balance</div>
								<div>
									{tiltScore(
										resultsRef.current.shoulderTilt.filter(
											p => (
												resultsRef.current.left.start
												<=
												p.t
												&&
												p.t
												<=
												resultsRef.current.left.end
											)
										).map(p=>({
											x:p.t - resultsRef.current.shoulderTilt[0].t,
											y:roundDecimal(p.angle),
											status:p.status
										}))
									).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">R. Balance</div>
								<div>
									{tiltScore(
										resultsRef.current.shoulderTilt.filter(
											p => (
												resultsRef.current.right.start
												<=
												p.t
												&&
												p.t
												<=
												resultsRef.current.right.end
											)
										).map( p => ({
											x:p.t - resultsRef.current.shoulderTilt[0].t,
											y:roundDecimal(p.angle),
											status:p.status
										}))
									).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">L. Time</div>
								<div>
									{((resultsRef.current.left.end - resultsRef.current.left.start)/100).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">R. Time</div>
								<div>
									{((resultsRef.current.right.end - resultsRef.current.right.start)/100).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">L. Knee Height</div>
								<div>
									{kneeHeightScore(
										resultsRef.current.left.kneePosition,
										guidesRef.current.idealKneeHeight,
										guidesRef.current.bottomY
									).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">R. Knee Height</div>
								<div>
									{kneeHeightScore(
										resultsRef.current.right.kneePosition,
										guidesRef.current.idealKneeHeight,
										guidesRef.current.bottomY
									).toFixed(0)}
								</div>
							</div>
							<div className="d-flex justify-content-between" style={{borderTop:'1px solid #ddd'}}>
								<div className="mr-2">Total Score</div>
								<div>
									{calculateScores().average.toFixed()}
								</div>
							</div>
							<Button 
								onClick={handleAddToProgress}
								variant="outline-success" 
								className="my-3"
								block
							>
								Add to {isClient?'':"Client's"} Progress Metrics
							</Button>
							<div className="mt-5 d-flex">
								<Button 
									className="mr-2"
									onClick={restartDemo}
									variant="outline-dark"
								>
									Restart
								</Button>
								{!isWebview()&&
									<Button 
										onClick={()=>{
											dispatch(setShow(false))
										}}
										variant="outline-danger" 
									>
										Close
									</Button>
								}
							</div>
						</div>
					</div>
				</div>
			}
			{showHowTo&&
				<HowTo onHide={()=>{setShowHowTo(false)}}>
					<strong>What is this?</strong>
					<p>
						With only a webcam, our system will automatically analyze your single leg balance. 
					</p>
					<strong>How do I use this?</strong>
					<ul>
						<li><span className="mr-2">1.</span>Press the green <span id={styles.StartHowTo}>Start</span> button & <strong>wait</strong> till it turns into a red <span id={styles.StopHowTo}>Stop</span> button (the first time always takes a few seconds)</li>
						<li><span className="mr-2">2.</span>Step back until your <strong>full body is in view</strong> of the camera and make sure to <strong>face the camera straight on</strong></li>
						<li><span className="mr-2">3.</span>When ready, <strong>hold arms out in a "T"</strong> and a <strong>3 second countdown</strong> will appear</li>
						<li><span className="mr-2">4.</span>When you see <span className="text-success"><strong>GO!</strong></span> lift one foot above the bottom line and try to <strong>balance for 10 seconds</strong></li>
						<li><span className="mr-2">5.</span>Switch sides and repeat</li>
						<li><span className="mr-2">6.</span>View your results and have fun trying to get a better score!</li>
						<li><span className="mr-2">7.</span>If the <span id={styles.RestartHowTo}>Restart</span> button is not in view <strong>scroll down</strong></li>
					</ul>
				</HowTo>
			}
		</div>
	)
}

export default BalanceDemo